/**
 * BE-1 (Sprint 2 rework) — Project Activity feed entities.
 * Strict-only schemas, UUID refs + denorm refs map for FE.
 */
import { z } from 'zod';

export const ProjectActivityKind = z.enum([
  'stage_change',
  'health_change',
  'milestone_reached',
  'billing_pct_updated',
  'task_completed',
  'task_overdue',
]);
export type ProjectActivityKind = z.infer<typeof ProjectActivityKind>;

export const ProjectActivityRefKind = z.enum(['user', 'milestone', 'task', 'invoice', 'none']);
export type ProjectActivityRefKind = z.infer<typeof ProjectActivityRefKind>;

/** Structural payload — enum/UUID/scalar only, no freeform. FE resolves via i18n. */
export const ProjectActivityPayload = z.object({
  /** for stage_change: previous stage code (enum) */
  from_stage: z.string().nullable().optional(),
  /** for stage_change: target stage code (enum) */
  to_stage: z.string().nullable().optional(),
  /** for health_change: previous health code */
  from_health: z.string().nullable().optional(),
  /** for health_change: target health code */
  to_health: z.string().nullable().optional(),
  /** for billing_pct_updated: numeric delta */
  pct_delta: z.string().nullable().optional(),
});
export type ProjectActivityPayload = z.infer<typeof ProjectActivityPayload>;

export const ProjectActivityEvent = z.object({
  id: z.string(),
  project_id: z.string().uuid(),
  kind: ProjectActivityKind,
  occurred_at: z.string().datetime(),
  actor_user_id: z.string().uuid().nullable(),
  ref_id: z.string().nullable(),
  ref_kind: ProjectActivityRefKind,
  payload: ProjectActivityPayload,
});
export type ProjectActivityEvent = z.infer<typeof ProjectActivityEvent>;

/** Denorm refs: name lookup table for FE rendering. UUID-only on event, name here. */
export const ProjectActivityRefs = z.object({
  users: z.record(z.string(), z.object({ id: z.string(), name: z.string() })),
  milestones: z.record(z.string(), z.object({ id: z.string(), name: z.string() })),
  tasks: z.record(z.string(), z.object({ id: z.string(), subject: z.string() })),
});
export type ProjectActivityRefs = z.infer<typeof ProjectActivityRefs>;

export const ProjectActivityResponse = z.object({
  project_id: z.string().uuid(),
  items: z.array(ProjectActivityEvent),
  refs: ProjectActivityRefs,
});
export type ProjectActivityResponse = z.infer<typeof ProjectActivityResponse>;
